'use client';
import { useEffect } from 'react';

/**
 * 레이어 공통 동작 — **뒤 화면 스크롤 잠금 + Esc 로 닫기**(2026-08-22 사용자 확정).
 *
 * 닫는 방법은 **X·닫기 버튼과 Esc 뿐**이다. **딤(바깥) 클릭으로는 닫지 않는다** —
 * 주소를 고르거나 약관을 읽는 중 딤을 스쳐 닫히면 처음부터 다시 해야 한다.
 * 스크롤을 막는 이유는 레이어를 보는 중 뒤가 움직이면 보던 자리를 잃기 때문이다.
 * 스크롤바가 사라지며 폭이 튀는 것을 막으려고 사라진 스크롤바 폭만큼 오른쪽 여백을 채운다.
 * **모바일 메뉴(햄버거 서랍)도 이걸 쓴다** — 레이어와 같은 규칙이다(2026-09-03).
 *
 * `onClose` 를 주지 않으면 스크롤 잠금만 한다(닫을 수 없는 레이어).
 */
export function useLayerLock(open: boolean, onClose?: () => void) {
  useEffect(() => {
    if (!open) return;
    /*
     * **모바일에서는 `overflow: hidden` 만으로 안 막힌다**(2026-09-03 사용자 제보 —
     * 모바일 메뉴를 펼친 채 손가락을 움직이면 뒤 화면이 스크롤됐다). iOS 사파리를 비롯한
     * 여러 모바일 브라우저가 body 의 overflow 를 무시하고 문서를 그대로 굴린다.
     *
     * 그래서 **문서를 그 자리에 못 박는다** — 지금 스크롤 위치만큼 위로 끌어올린 `position: fixed`.
     * 닫을 때 그 위치로 되돌린다(안 되돌리면 맨 위로 튄다).
     */
    const y = window.scrollY;
    const { overflow, paddingRight, position, top, width } = document.body.style;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.width = '100%';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      document.body.style.position = position;
      document.body.style.top = top;
      document.body.style.width = width;
      window.scrollTo(0, y);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !onClose) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open, onClose]);
}
